
import moment from 'moment'
import numbro from 'numbro'
import { mapGetters, mapActions } from 'vuex'

import TextBox from '@/components/TextBox.vue'
import { formatFullAmount } from '@/utils/compactAmount.js'
import BudgetSimulation from '@/components/budgetSimulator/BudgetSimulation.vue'
import NgtorchRunChip from '@/components/NgtorchRunChip.vue'
import { currentModelReport } from '@/mixins/currentModelReport.js'
import { ngtorchRuns } from '@/mixins/ngtorchRuns.js'
import { parsedResults, outputNames } from '@/utils/budgetSimulator.js'
import { buildPerKpi, downloadPerKpi } from '@/utils/modelReportXlsx.js'

export default {
  mixins: [currentModelReport, ngtorchRuns],
  components: {
    TextBox,
    BudgetSimulation,
    NgtorchRunChip
  },
  data () {
    return {
      currentKpi: null,
      downloadingXlsx: false,
      downloadXlsxFailed: false
    }
  },
  computed: {
    ...mapGetters('clients', ['currentClient', 'currentClientObject']),
    ...mapGetters('simulations', ['simulationsLoading', 'budgetSimulations', 'budgetSimulationPoints']),
    simulation () {
      return this.budgetSimulations.find(simulation => simulation.id === this.$route.params.id) || null
    },
    points () {
      return this.simulation ? this.budgetSimulationPoints(this.simulation.id) : []
    },
    ngtorchRunsPlans () {
      return this.points.map(point => ({ ...point, clientId: this.currentClient }))
    },
    referenceModelConfig () {
      const client = this.currentClientObject
      if (client && client.plan_reference_model) return client.plan_reference_model.config
      return client && client.reference_model ? client.reference_model.config : {}
    },
    spendUnitName () {
      return this.referenceModelConfig.invest_unit_name || 'USD'
    },
    customKpiName () {
      return (this.currentClientObject && this.currentClientObject.customKpiName) || ''
    },
    gridSize () {
      return this.simulation ? (this.simulation.grid || []).length : 0
    },
    donePoints () {
      return this.points.filter(point => !!parsedResults(point)).length
    },
    isComplete () {
      return this.gridSize > 0 && this.donePoints >= this.gridSize
    },
    kpiNames () {
      const finished = this.points.map(point => parsedResults(point)).find(results => results && results.sales)
      return outputNames(finished, this.currentClientObject, this.referenceModelConfig)
    },
    // The shares of a Frozen mix, largest first, by display name.
    frozenMix () {
      if (this.simulation.mix_mode !== 'frozen' || !this.simulation.mix) return []
      const mix = this.simulation.mix
      const pretty = (this.currentClientObject.pretty_names && this.currentClientObject.pretty_names.media) || {}
      return Object.keys(mix)
        .filter(channel => mix[channel] > 0)
        .sort((a, b) => mix[b] - mix[a])
        .map(channel => ({ channel: channel, label: pretty[channel] || channel, value: mix[channel] }))
    },
    metrics () {
      const grid = this.simulation.grid || []
      const total = grid.length
      return [
        {
          key: 'mode',
          icon: 'tune-variant',
          label: this.$t('views.BudgetSimulationDetails.mixMode'),
          value: this.simulation.mix_mode === 'frozen'
            ? this.$t('components.budgetSimulator.CreateBudgetSimulation.frozen')
            : this.$t('components.budgetSimulator.CreateBudgetSimulation.free')
        },
        {
          key: 'reference',
          icon: 'cash-multiple',
          label: this.$t('views.BudgetSimulationDetails.referenceBudget', { unit: this.spendUnitName }),
          value: this.formatValue(this.simulation.reference_budget)
        },
        {
          key: 'grid',
          icon: 'chart-timeline-variant',
          label: this.$t('views.BudgetSimulationDetails.grid', { count: total }),
          value: total > 0 ? `${grid[0].pct}% – ${grid[total - 1].pct}%` : '-',
          // The budgets those percentages come to, shown under them.
          amounts: total > 0 ? `${this.formatValue(grid[0].budget)} – ${this.formatValue(grid[total - 1].budget)}` : ''
        }
      ]
    }
  },
  methods: {
    ...mapActions('simulations', ['loadSimulations']),
    ...mapActions('clients', ['setCurrentClient']),
    formatDate (date) {
      return moment(date).format('ll')
    },
    formatFullAmount: formatFullAmount,
    formatValue (value) {
      return numbro(value || 0).format({ average: true, mantissa: 1, optionalMantissa: true })
    },
    // XLSX download: the simulation's settings and every chart's data, in one workbook per KPI of
    // the selector (a ZIP of them when there is more than one). The charts' sheets come from
    // BudgetSimulation, which holds their data and reads them off its own currentKpi, so that is
    // the KPI switched for each workbook. SheetJS is loaded only on click so it stays out of the
    // route's bundle.
    async downloadXlsx () {
      const charts = this.$refs.budgetSimulation
      if (this.downloadingXlsx || !charts) return
      this.downloadingXlsx = true
      try {
        const kpis = this.kpiNames.length > 1 ? this.kpiNames : [this.currentKpi]
        const books = buildPerKpi(kpis, charts.currentKpi, kpi => { charts.currentKpi = kpi }, kpi => {
          const sheets = [{ title: this.$t('components.ModelReportComponent.summary'), rows: this.xlsxSummaryRows(kpi) }]
          if (this.frozenMix.length > 0) {
            sheets.push({
              title: this.$t('components.budgetSimulator.BudgetSimulation.xlsxFrozenMix'),
              rows: [[this.$t('components.ModelReportComponent.xlsxChannel'), this.$t('components.budgetSimulator.BudgetSimulation.xlsxShare')]]
                .concat(this.frozenMix.map(share => [share.label, share.value]))
            })
          }
          return sheets.concat(charts.buildXlsxSheets())
        })
        const XLSX = await import('xlsx')
        const period = [this.simulation.date_from, this.simulation.date_to].filter(Boolean).join('_')
        const name = `[${this.$t('views.App.budgetSimulator')}] ${this.simulation.name}`
        downloadPerKpi(XLSX, books, kpi => [this.currentClientObject.name, name, kpi, period].filter(Boolean).join(' - '))
      } catch (error) {
        console.error('Failed to download the budget simulation as XLSX:', error)
        this.downloadXlsxFailed = true
      } finally {
        this.downloadingXlsx = false
      }
    },
    xlsxSummaryRows (kpi) {
      const tx = key => this.$t('components.ModelReportComponent.' + key)
      const grid = this.simulation.grid || []
      const rows = [
        [tx('xlsxMetric'), tx('xlsxValue')],
        [tx('xlsxModel'), this.currentClientObject.name],
        [this.$t('components.budgetSimulator.BudgetSimulation.xlsxSimulation'), this.simulation.name],
        [this.$t('components.budgetSimulator.BudgetSimulation.kpi'), kpi],
        [tx('xlsxStartDate'), this.simulation.date_from],
        [tx('xlsxEndDate'), this.simulation.date_to]
      ]
      this.metrics.forEach(metric => {
        if (metric.key === 'reference') {
          // The exact amount, not the abbreviation the box shows.
          rows.push([metric.label, this.simulation.reference_budget])
        } else {
          rows.push([metric.label, metric.value])
        }
      })
      if (grid.length > 0) {
        rows.push([this.$t('components.budgetSimulator.BudgetSimulation.xlsxGridBudgets', { unit: this.spendUnitName }), grid.map(point => point.budget).join(', ')])
      }
      return rows
    },
    back () {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push({ name: 'budgetSimulator' })
      }
    }
  },
  watch: {
    // The Total when the outputs add up to one, otherwise the first visible KPI.
    kpiNames: {
      immediate: true,
      handler (names) {
        if (names.indexOf(this.currentKpi) === -1) {
          this.currentKpi = names.indexOf('Total') !== -1 ? 'Total' : (names[0] || null)
        }
      }
    },
    // Arriving from a notification selects the model the simulation belongs to.
    '$route.params.client': {
      immediate: true,
      handler (client) {
        if (client) {
          this.setCurrentClient(client)
        }
      }
    },
    currentClient: {
      immediate: true,
      handler () {
        if (this.currentClient) {
          this.loadSimulations({ client: this.currentClient })
        }
      }
    }
  }
}
